import React, { useState } from 'react';
import { HardHat, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Subunternehmer = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  
  const [formData, setFormData] = useState({
    firmenname: '',
    ansprechpartner: '',
    strasse: '',
    plz_ort: '',
    land: 'Deutschland',
    telefon: '',
    email: '',
    mitarbeiter_anzahl: '',
    gewerk: '',
    nachricht: '',
    auslaendisch: false,
    unterlagen: {
      freistellungsbescheinigung: false,
      unbedenklichkeit_sgb: false,
      ubbs: false,
      hwk_anmeldung: false,
      hwk_dienstleistungsanzeige: false,
      haftpflichtversicherung: false
    }
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'nachricht') {
      setCharCount(value.length);
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      land: value
    });
  };

  const handleAuslaendischToggle = (checked) => {
    setFormData({
      ...formData,
      auslaendisch: checked,
      unterlagen: {
        ...formData.unterlagen,
        ubbs: false,
        hwk_anmeldung: false,
        hwk_dienstleistungsanzeige: false
      }
    });
  };

  const handleUnterlagenChange = (field, checked) => {
    setFormData({
      ...formData,
      unterlagen: {
        ...formData.unterlagen,
        [field]: checked
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert mitarbeiter_anzahl to number
      const submitData = {
        ...formData,
        mitarbeiter_anzahl: parseInt(formData.mitarbeiter_anzahl) || 0
      };

      const response = await axios.post(`${API}/subunternehmer`, submitData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast({
          title: '✅ Bewerbung gesendet!',
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          firmenname: '',
          ansprechpartner: '',
          strasse: '',
          plz_ort: '',
          land: 'Deutschland',
          telefon: '',
          email: '',
          mitarbeiter_anzahl: '',
          gewerk: '',
          nachricht: '',
          auslaendisch: false,
          unterlagen: {
            freistellungsbescheinigung: false,
            unbedenklichkeit_sgb: false,
            ubbs: false,
            hwk_anmeldung: false,
            hwk_dienstleistungsanzeige: false,
            haftpflichtversicherung: false
          }
        });
        setCharCount(0);
        
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: '❌ Fehler',
        description: error.response?.data?.detail || 'Bewerbung konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="subunternehmer" className="py-20 bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Prominent Banner */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 mb-12 text-center shadow-2xl">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-full mb-6">
            <HardHat className="text-white" size={40} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Wir suchen zuverlässige Subunternehmer!
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Sie sind ein erfahrenes Unternehmen im Bereich Gebäudetechnik? 
            Werden Sie Teil unseres Netzwerks und profitieren Sie von langfristigen Partnerschaften!
          </p>
        </div>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Bewerbung erfolgreich gesendet!</h3>
            <p className="text-gray-600">Vielen Dank! Wir melden uns in Kürze bei Ihnen.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* LEFT COLUMN - Message */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Ihre Nachricht / Vorstellung</h3>
                
                <div className="mb-2">
                  <label htmlFor="nachricht" className="block text-sm font-medium text-gray-700 mb-2">
                    Stellen Sie sich und Ihr Unternehmen vor *
                  </label>
                  <Textarea
                    id="nachricht"
                    name="nachricht"
                    required
                    value={formData.nachricht}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie Ihr Unternehmen, Ihre Erfahrung und warum Sie mit uns zusammenarbeiten möchten..."
                    rows={15}
                    maxLength={5000}
                    className="w-full resize-none"
                  />
                  <div className="text-sm text-gray-500 mt-2 text-right">
                    {charCount} / 5000 Zeichen
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - Company & Contact Data */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Firmen- & Kontaktdaten</h3>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="firmenname" className="block text-sm font-medium text-gray-700 mb-2">
                      Firmenname *
                    </label>
                    <Input
                      id="firmenname"
                      name="firmenname"
                      type="text"
                      required
                      value={formData.firmenname}
                      onChange={handleChange}
                      placeholder="Ihre Firma GmbH"
                    />
                  </div>

                  <div>
                    <label htmlFor="ansprechpartner" className="block text-sm font-medium text-gray-700 mb-2">
                      Ansprechpartner / Name *
                    </label>
                    <Input
                      id="ansprechpartner"
                      name="ansprechpartner"
                      type="text"
                      required
                      value={formData.ansprechpartner}
                      onChange={handleChange}
                      placeholder="Max Mustermann"
                    />
                  </div>

                  <div>
                    <label htmlFor="strasse" className="block text-sm font-medium text-gray-700 mb-2">
                      Straße & Hausnummer *
                    </label>
                    <Input
                      id="strasse"
                      name="strasse"
                      type="text"
                      required
                      value={formData.strasse}
                      onChange={handleChange}
                      placeholder="Musterstraße 123"
                    />
                  </div>

                  <div>
                    <label htmlFor="plz_ort" className="block text-sm font-medium text-gray-700 mb-2">
                      PLZ & Ort *
                    </label>
                    <Input
                      id="plz_ort"
                      name="plz_ort"
                      type="text"
                      required
                      value={formData.plz_ort}
                      onChange={handleChange}
                      placeholder="80339 München"
                    />
                  </div>

                  <div>
                    <label htmlFor="land" className="block text-sm font-medium text-gray-700 mb-2">
                      Land *
                    </label>
                    <Select value={formData.land} onValueChange={handleSelectChange}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Land auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Deutschland">Deutschland</SelectItem>
                        <SelectItem value="Österreich">Österreich</SelectItem>
                        <SelectItem value="Schweiz">Schweiz</SelectItem>
                        <SelectItem value="Andere">Andere</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon *
                    </label>
                    <Input
                      id="telefon"
                      name="telefon"
                      type="tel"
                      required
                      value={formData.telefon}
                      onChange={handleChange}
                      placeholder="+49 89 12345678"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@firma.de"
                    />
                  </div>

                  <div>
                    <label htmlFor="mitarbeiter_anzahl" className="block text-sm font-medium text-gray-700 mb-2">
                      Anzahl eigene Mitarbeiter *
                    </label>
                    <Input
                      id="mitarbeiter_anzahl"
                      name="mitarbeiter_anzahl"
                      type="number"
                      required
                      min="0"
                      value={formData.mitarbeiter_anzahl}
                      onChange={handleChange}
                      placeholder="z.B. 15"
                    />
                  </div>

                  <div>
                    <label htmlFor="gewerk" className="block text-sm font-medium text-gray-700 mb-2">
                      Ausführendes Gewerk laut Gewerbeanmeldung *
                    </label>
                    <Input
                      id="gewerk"
                      name="gewerk"
                      type="text"
                      required
                      value={formData.gewerk}
                      onChange={handleChange}
                      placeholder="z.B. Heizungsbau, Sanitärinstallation"
                    />
                  </div>
                </div>

                {/* Unterlagen Section */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="text-lg font-bold text-slate-900 mb-4">
                    Folgende Unterlagen liegen in aktueller und gültiger Form vor:
                  </h4>

                  {/* Toggle for Ausländisches Unternehmen */}
                  <div className="flex items-center space-x-3 mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <Checkbox
                      id="auslaendisch"
                      checked={formData.auslaendisch}
                      onCheckedChange={handleAuslaendischToggle}
                    />
                    <label
                      htmlFor="auslaendisch"
                      className="text-sm font-semibold text-slate-900 cursor-pointer"
                    >
                      Ausländisches Unternehmen?
                    </label>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="freistellungsbescheinigung"
                        checked={formData.unterlagen.freistellungsbescheinigung}
                        onCheckedChange={(checked) => handleUnterlagenChange('freistellungsbescheinigung', checked)}
                      />
                      <label
                        htmlFor="freistellungsbescheinigung"
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Freistellungsbescheinigung §48b EStG
                      </label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="unbedenklichkeit_sgb"
                        checked={formData.unterlagen.unbedenklichkeit_sgb}
                        onCheckedChange={(checked) => handleUnterlagenChange('unbedenklichkeit_sgb', checked)}
                      />
                      <label
                        htmlFor="unbedenklichkeit_sgb"
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Unbedenklichkeitsbescheinigung §13 SGB IV (Sozialkasse / SOKA)
                      </label>
                    </div>

                    {!formData.auslaendisch ? (
                      <>
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="ubbs"
                            checked={formData.unterlagen.ubbs}
                            onCheckedChange={(checked) => handleUnterlagenChange('ubbs', checked)}
                          />
                          <label
                            htmlFor="ubbs"
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            Aktuelle UBBS (Unbedenklichkeitsbescheinigung Berufsgenossenschaft)
                          </label>
                        </div>

                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id="hwk_anmeldung"
                            checked={formData.unterlagen.hwk_anmeldung}
                            onCheckedChange={(checked) => handleUnterlagenChange('hwk_anmeldung', checked)}
                          />
                          <label
                            htmlFor="hwk_anmeldung"
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            HWK-Anmeldung (Handwerkskammer)
                          </label>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="hwk_dienstleistungsanzeige"
                          checked={formData.unterlagen.hwk_dienstleistungsanzeige}
                          onCheckedChange={(checked) => handleUnterlagenChange('hwk_dienstleistungsanzeige', checked)}
                        />
                        <label
                          htmlFor="hwk_dienstleistungsanzeige"
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          HWK Dienstleistungsanzeige (für ausländische Firmen)
                        </label>
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="haftpflichtversicherung"
                        checked={formData.unterlagen.haftpflichtversicherung}
                        onCheckedChange={(checked) => handleUnterlagenChange('haftpflichtversicherung', checked)}
                      />
                      <label
                        htmlFor="haftpflichtversicherung"
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Haftpflichtversicherung (aktuell und gültig)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 text-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold text-lg px-12 py-6"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Wird gesendet...
                  </span>
                ) : (
                  <span className="flex items-center">
                    Jetzt bewerben
                    <Send size={20} className="ml-2" />
                  </span>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
